import { Modal, WizardView } from '@salecycle/ui-component-library';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import { ICampaign } from 'entities/campaign';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { generatePath } from 'react-router';
import { Route, Switch, useHistory, useParams } from 'react-router-dom';
import {
  arePreviousStepsValid,
  buildNewCampaignsPath,
  redirectToLastValidStep,
} from 'sections/campaigns/new/sections/new-campaign/utils/helpers';
import { CampaignRoute } from 'sections/campaigns/ui/Campaigns';
import { OffsiteCampaignEndpoints } from 'sections/offsite-campaigns/constants/api-endpoints';
import { OffsiteCampaignRoute } from 'sections/offsite-campaigns/routes/offsite-campaign-routes';
import { IOffsiteCampaign } from '../../../../../offsite-campaigns/entities/offsiteCampaign';
import { CampaignApiEndpoints } from '../../../../utils/api-endpoints';
import { useNewCampaignDispatch, useNewCampaignState } from '../context/UseNewCampaignContext';
import { getSteps, stepRoutes } from '../data/NewCampaignConstants';
import { ICombinedCampaign } from '../entities';
import { DetailsStep } from './steps/details-page/DetailsStep';
import { FeaturesStep } from './steps/features-page/FeaturesStep';
import { ObjectivesStep } from './steps/objective-page/ObjectivesStep';

export const NewCampaignWizard: React.FC = () => {
  const { t } = useTranslation('campaigns');
  const { push } = useHistory();
  const { siteId, campaignWizardPage } = useParams<{ siteId: string; campaignWizardPage: string }>();
  const { validSteps, combinedCampaign, createdCombinedCampaign, success } = useNewCampaignState();
  const steps = getSteps(t);
  const dispatch = useNewCampaignDispatch();
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);
  const [modalVisible, setModalVisible] = React.useState(false);

  const saveCampaignMutation = useMutation((combinedCampaign: ICombinedCampaign) =>
    RestApi.post<{ id: string }, ICampaign>(
      generatePath(CampaignApiEndpoints.MPS_POST, { siteId }),
      combinedCampaign.campaign,
    ),
  );

  const saveOffsiteCampaignMutation = useMutation((combinedCampaign: ICombinedCampaign) =>
    RestApi.post<{ id: string }, IOffsiteCampaign>(
      generatePath(OffsiteCampaignEndpoints.OFFSITE_POST, { siteId }),
      combinedCampaign.offsiteCampaign,
    ),
  );

  const handleSaveCampaign = async () => {
    if (combinedCampaign.objective === 'Recover Abandoned Sales') {
      combinedCampaign.offsiteCampaign.siteId = siteId;
      saveOffsiteCampaignMutation.mutate(combinedCampaign, {
        onSuccess: data => {
          push(generatePath(OffsiteCampaignRoute.OFFSITE_EDIT, { siteId, campaignId: data.data.id }), {
            showAddNewSegmentModal: true,
          });
        },
        onError: (error: RestApiError, variables, context) => {
          dispatch({ type: 'CLEAR_ERROR' });
          dispatch({
            type: 'CREATE_CAMPAIGN_FAILED',
            payload: { createdCombinedCampaign: null, success: false, error: true, errorMessage: error.message },
          });
        },
      });
    } else {
      saveCampaignMutation.mutate(combinedCampaign, {
        onSuccess: data => {
          push(generatePath(CampaignRoute.MPS_EDIT, { siteId, campaignId: data.data.id }), {
            showAddNewRulesetModal: true,
          });
        },
        onError: (error: RestApiError, variables, context) => {
          dispatch({ type: 'CLEAR_ERROR' });
          dispatch({
            type: 'CREATE_CAMPAIGN_FAILED',
            payload: { createdCombinedCampaign: null, success: false, error: true, errorMessage: error.message },
          });
        },
      });
    }
  };

  useEffect(() => () => dispatch({ type: 'RESET_WIZARD' }), [dispatch]);

  useEffect(() => {
    setCurrentStepIndex(stepRoutes.indexOf(`/${campaignWizardPage}`) + 1);
  }, [campaignWizardPage]);

  useEffect(() => {
    const prevStepsValid = arePreviousStepsValid(validSteps, currentStepIndex);

    if (!prevStepsValid) {
      redirectToLastValidStep(steps, validSteps, push);
    }
  }, [currentStepIndex, push, steps, validSteps]);

  useEffect(() => {
    if (success) {
      if (combinedCampaign.objective === 'Recover Abandoned Sales') {
        push(
          generatePath(OffsiteCampaignRoute.OFFSITE_EDIT, {
            siteId,
            campaignId: createdCombinedCampaign.offsiteCampaign.id,
          }),
          {
            showAddNewSegmentModal: true,
          },
        );
      } else {
        push(generatePath(CampaignRoute.MPS_EDIT, { siteId, campaignId: createdCombinedCampaign.campaign.id }), {
          showAddNewRulesetModal: true,
        });
      }
    }
  }, [
    success,
    createdCombinedCampaign.campaign.id,
    createdCombinedCampaign.offsiteCampaign.id,
    combinedCampaign.objective,
    push,
    siteId,
  ]);

  const onPrevButtonClicked = () => {
    if (currentStepIndex !== 1) {
      push(buildNewCampaignsPath(siteId, steps[currentStepIndex - 2].route));
    } else {
      push(generatePath(CampaignRoute.MPS_LIST, { siteId }));
    }
  };

  const onNextButtonClicked = () => {
    if (currentStepIndex !== steps.length) {
      push(buildNewCampaignsPath(siteId, steps[currentStepIndex].route));
    } else {
      setModalVisible(true);
    }
  };

  const isValidStep = () => {
    const stepValid = !!validSteps.find(x => x === currentStepIndex);
    return stepValid;
  };

  function getName(combinedCampaign: ICombinedCampaign): string {
    if (combinedCampaign.objective === 'Recover Abandoned Sales') {
      return (combinedCampaign?.offsiteCampaign && combinedCampaign?.offsiteCampaign.name) || '';
    } else {
      return (combinedCampaign?.campaign && combinedCampaign?.campaign.name) || '';
    }
  }

  return (
    <>
      <WizardView
        currentStep={currentStepIndex}
        steps={steps.map(step => {
          return { label: step.label };
        })}
        onPrevButtonClicked={onPrevButtonClicked}
        onNextButtonClicked={onNextButtonClicked}
        nextButtonDisabled={!isValidStep()}
      >
        <Switch>
          <Route
            exact={true}
            path={`/sites/:siteId/campaigns/campaigns/newcampaign${steps[0].route}`}
            render={() => currentStepIndex === 1 && <ObjectivesStep currentStep={currentStepIndex} />}
          />
          <Route
            exact={true}
            path={`/sites/:siteId/campaigns/campaigns/newcampaign${steps[1].route}`}
            render={() => currentStepIndex === 2 && <FeaturesStep currentStep={currentStepIndex} />}
          />
          <Route
            exact={true}
            path={`/sites/:siteId/campaigns/campaigns/newcampaign${steps[2].route}`}
            render={() => currentStepIndex === 3 && <DetailsStep currentStep={currentStepIndex} />}
          />
        </Switch>
      </WizardView>
      <Modal
        variation="clean"
        isOpen={modalVisible}
        heading={t('newCampaigns.modal.heading')}
        okButtonLabel={t('newCampaigns.modal.okButton.label')}
        onCancelButtonClicked={() => setModalVisible(false)}
        onOkButtonClicked={handleSaveCampaign}
      >
        <p>
          {t('newCampaigns.modal.messages.confirmationMessage')} {getName(combinedCampaign)}
        </p>
        <p>{t('newCampaigns.modal.messages.confirmationCancelOrCreate')}</p>
      </Modal>
    </>
  );
};

export default NewCampaignWizard;
